import {
	EnodeHvacReturnZod,
	EnodeHvacZod,
	HvacAction,
	SmartResponse,
} from '@vae/server/src/interfaces/eNode/eNodeHVAC.interface'
import { Schedule } from '@vae/server/src/interfaces/eNode/eNodeSchedule.interface'
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom'

import { logEvent } from '../../../analytics/utils/firebaseUtils'
import useUser from '../../../contexts/hooks/useUser'
import { ObjectContext } from '../../../contexts/objectContext/ObjectContext'
import { trpc } from '../../../modules/trpc'
import { useHvacs } from './hooks/useHvac'

interface HvacContextType {
	hvac: EnodeHvacZod | null | undefined
	smartPolicy: boolean | undefined
	setHvacValue: (
		hvacId: string,
		thermostatState: {
			mode: 'OFF' | 'HEAT' | 'COOL' | 'AUTO'
			heatSetpoint?: number | undefined | null
			coolSetpoint?: number | undefined | null
		}
	) => void
	setSmartPolicy: (value: boolean, hvacId: string) => void
	setSchedule: (schedule: Schedule, scheduleType: string) => void
	mutationLoading: boolean
	setMutationLoading: React.Dispatch<React.SetStateAction<boolean>>
	awayMode: {
		enabled: boolean
		savedTemperature: number
	}
	setAwayValue: (value: number, enabled: boolean) => void
	thermoStatus: {
		icon: React.JSX.Element
		text: string
		color?: string | undefined
	}
	error: string | null
	unlinkHvac: (hvac: EnodeHvacZod | undefined) => void
	smartStatus: SmartResponse | undefined
	onlyDaySchedule: Schedule | null | undefined
	dayAndNightSchedule: Schedule | null | undefined
	hvacs: EnodeHvacReturnZod | undefined | null
	modeSwitchLoading: boolean
	setModeSwitchLoading: React.Dispatch<React.SetStateAction<boolean>>
	actionStatus: HvacAction | undefined | null
}

export const HvacContext = createContext({} as HvacContextType)

interface Props {
	children?: React.ReactNode
}

const HvacContextProvider: React.FC<Props> = ({ children }) => {
	const utils = trpc.useContext()

	const object = useContext(ObjectContext)

	const history = useHistory()
	const location = useLocation()
	const hvacId = location.pathname.startsWith('/devices/hvac/')
		? location.pathname.split('/devices/hvac/')[1]
		: null

	const hvacData = useHvacs()
	const { user } = useUser()

	if (!hvacData) return null
	const { t } = useTranslation('pages_hvac')

	const [hvacs, setHvacs] = useState<EnodeHvacReturnZod | undefined | null>(undefined)
	const [hvacSmartPolicy, setHvacSmartPolicy] = useState<boolean | undefined>(undefined)
	const [dayAndNightSchedule, setDayAndNightSchedule] = useState<Schedule | null | undefined>(null)
	const [onlyDaySchedule, setOnlyDaySchedule] = useState<Schedule | null | undefined>(null)
	const [mutationLoading, setMutationLoading] = useState<boolean>(false)
	const [modeSwitchLoading, setModeSwitchLoading] = useState<boolean>(false)
	const [activeHvac, setActiveHvac] = useState<EnodeHvacZod>()
	const [enodeError, setEnodeError] = useState<string | null>(null)
	const [actionId, setActionId] = useState<string | null>(null)
	const [awayMode, setAwayMode] = useState({
		enabled: false,
		savedTemperature: 0,
	})

	const { data: userSettings } = trpc.user.userSettings.get.useQuery(undefined, {
		enabled: !!user?.id,
	})

	const [offlineHvac, setOfflineHvac] = useState<EnodeHvacZod | null | undefined>(null)
	const updateSmartPolicy = trpc.eNode.hvac.setSmartPolicy.useMutation({
		onMutate: () => {
			setMutationLoading(true)
		},
		onSuccess: () => {
			setTimeout(() => {
				setMutationLoading(false)
			}, 1000)
		},

		onError: () => {
			setMutationLoading(false)
			setEnodeError('Error updating smart heating' + enodeError)
			console.error('Error updating smart heating')
		},
	})
	const deleteSchedules = trpc.eNode.schedules.deleteSchedule.useMutation({
		onMutate: () => {
			logEvent('click-delete-schedule')
		},
		onSuccess: () => {
			utils.eNode.schedules.getAllSchedules.invalidate({ hashedPid: user!.hashedPid })
		},
		onError: () => {
			console.error('Error deleting schedule')
		},
	})

	const deleteAllSchedules = async (hvacId: string) => {
		if (!hvacData.hvacSchedules) return

		const schedules = hvacData.hvacSchedules.data.filter(schedule => schedule.targetId === hvacId)
		for (const schedule of schedules) {
			await deleteSchedules.mutateAsync({ scheduleId: schedule.id, hashedPid: user!.hashedPid })
		}
	}
	const unlink = trpc.eNode.user.disconnect.vendorType.useMutation({
		onSuccess: () => {
			utils.eNode.hvac.getAll.invalidate()
			history.goBack()
		},

		onError: () => {
			console.error('Error delinking HVAC')
		},
	})

	const updateSchedule = trpc.eNode.schedules.updateSchedule.useMutation({
		onMutate: () => {
			setMutationLoading(true)
		},
		onSuccess: data => {
			// Update the local state values based on the updated schedule
			const updatedSchedule = data
			if (updatedSchedule) {
				if (updatedSchedule.rules && updatedSchedule.rules.length === 1) {
					setOnlyDaySchedule(updatedSchedule)
				} else if (updatedSchedule.rules && updatedSchedule.rules.length === 2) {
					setDayAndNightSchedule(updatedSchedule)
				}
			}
			setTimeout(() => {
				setMutationLoading(false)
			}, 1000)
		},
		onError: error => {
			setMutationLoading(false)
			setEnodeError('Error updating schedule: ' + error.message)
			console.error('Error updating schedule:', error)
		},
	})
	const unlinkHvac = (hvac: EnodeHvacZod | undefined | null) => {
		if (!user?.hashedPid || !hvac) return
		unlink.mutate({
			userId: user?.id,
			userHashedPid: user?.hashedPid,
			vendor: hvac?.vendor,
			vendorType: 'hvac',
		})

		deleteAllSchedules(hvac?.id ?? '')
	}

	const updateHeatingStatus = trpc.eNode.hvac.setDeviceMode.useMutation({
		onMutate: () => {
			setMutationLoading(true)
		},
		onSuccess: () => {
			setTimeout(() => {
				setMutationLoading(false)
			}, 3500)
		},
		onError: () => {
			setMutationLoading(false)
			setEnodeError('Error updating heating status')
			console.error('Error updating heating status')
		},
	})

	const cancelAction = trpc.eNode.hvac.cancelHvacAction.useMutation({
		onError: () => {
			console.error('Error cancelling action')
		},
	})

	const { data: smartPolicy } = trpc.eNode.hvac.getSmartPolicy.useQuery(
		{
			hvacId: hvacId ?? '',
		},
		{
			enabled: Boolean(hvacId),
			staleTime: 1000 * 60 * 1, // 1 minute
		}
	)

	const { data: smartStatus } = trpc.eNode.hvac.getSmartStatus.useQuery(
		{
			hashedPid: user?.hashedPid ?? '',
			hvacId: hvacId ?? '',
		},

		{ enabled: Boolean(user?.hashedPid && user?.hvacLinked && hvacId) }
	)

	const { data: actionStatus, refetch: refetchAction } = trpc.eNode.hvac.getAction.useQuery(
		{
			actionId: actionId || '',
		},
		{
			enabled: actionId !== null,
			onSuccess(data) {
				if (data?.state === 'CONFIRMED') {
					setOfflineHvac(prev => {
						if (!prev) return null
						return {
							...prev,
							thermostatState: data.target as EnodeHvacZod['thermostatState'],
						}
					})
					setActionId(null)
					setModeSwitchLoading(false)
				}
			},
		}
	)

	useEffect(() => {
		if (actionStatus?.state === 'PENDING') {
			const intervalId = setInterval(() => {
				refetchAction()
			}, 5000)

			return () => clearInterval(intervalId)
		}
	}, [actionStatus?.id])

	const updateObjectHvac = trpc.objects.edit.useMutation({
		onError: () => {
			console.error('Error updating away mode')
		},
	})

	useEffect(() => {
		setHvacs(hvacData.hvacs)
	}, [hvacData.hvacs])

	useEffect(() => {
		if (!hvacId) return
		setActiveHvac(hvacs?.data?.find(hvac => hvac.id === hvacId))
		setOfflineHvac(hvacs?.data?.find(hvac => hvac.id === hvacId))
		setHvacSmartPolicy(smartPolicy?.isEnabled)

		setAwayMode({
			enabled: object.hvacAwayMode,
			savedTemperature: object.savedTemperature || 20,
		})

		setDayAndNightSchedule(
			hvacData.hvacSchedules?.data.find(
				schedule => schedule.rules.length === 2 && schedule.targetId === hvacId
			)
		)
		setOnlyDaySchedule(
			hvacData.hvacSchedules?.data.find(
				schedule => schedule.rules.length === 1 && schedule.targetId === hvacId
			)
		)
	}, [hvacId, smartPolicy, smartStatus, hvacs, hvacData.hvacSchedules])

	const setHvacValue = (
		hvacId: string,
		thermostatState: {
			mode: 'OFF' | 'HEAT' | 'COOL' | 'AUTO'
			heatSetpoint?: number | null | undefined
			coolSetpoint?: number | null | undefined
		}
	) => {
		const currentHvac = hvacs?.data.find(hvac => hvac.id === hvacId)
		if (!currentHvac || !offlineHvac) {
			console.error('HVAC not found')
			return
		}
		if (actionStatus?.state === 'PENDING') {
			currentHvac.thermostatState = {
				...actionStatus.target,
			}
		}
		let isStateDifferent = true

		if (offlineHvac.thermostatState.mode === thermostatState.mode) {
			if (
				offlineHvac.thermostatState.mode === 'HEAT' &&
				offlineHvac.thermostatState.heatSetpoint === thermostatState.heatSetpoint
			) {
				isStateDifferent = false
			}
			if (
				offlineHvac.thermostatState.mode === 'COOL' &&
				offlineHvac.thermostatState.coolSetpoint === thermostatState.coolSetpoint
			) {
				isStateDifferent = false
			}
			if (
				offlineHvac.thermostatState.mode === 'AUTO' &&
				offlineHvac.thermostatState.heatSetpoint === thermostatState.heatSetpoint &&
				offlineHvac.thermostatState.coolSetpoint === thermostatState.coolSetpoint
			) {
				isStateDifferent = false
			}
		}

		if (isStateDifferent) {
			updateHeatingStatus.mutate(
				{
					hvacId: hvacId,
					hashedPid: user?.hashedPid || '',
					thermostatState,
				},
				{
					onSuccess: data => {
						setActionId(data?.id || null)
						setHvacs(prev => {
							if (!prev) return
							const updatedHvacs = prev.data.map(hvac => {
								if (hvac.id === hvacId) {
									return {
										...hvac,
										thermostatState,
									}
								}
								return hvac
							})
							return {
								...prev,
								data: updatedHvacs,
							}
						})
						setActiveHvac(prev => ({
							...prev!,
							thermostatState,
						}))
					},
					onError: (error, variables, context) => {
						console.error('onError', error, variables, context)
					},
				}
			)
		} else {
			if (!offlineHvac) return
			setHvacs(prev => {
				if (!prev) return
				const updatedHvacs = prev.data.map(hvac => {
					if (hvac.id === hvacId) {
						return {
							...offlineHvac,
						}
					}
					return hvac
				})
				return {
					...prev,
					data: updatedHvacs,
				}
			})
			setActiveHvac(offlineHvac)

			if (actionId) {
				cancelAction.mutate({
					hashedPid: user?.hashedPid || '',
					actionId: actionId,
				})
			}
		}
	}
	const setSmartPolicy = (value: boolean) => {
		if (!hvacId) return
		setHvacSmartPolicy(value)
		updateSmartPolicy.mutate({
			hvacId: hvacId,
			isEnabled: value,
		})
	}
	const setSchedule = (schedule: Schedule) => {
		if (!user) return

		updateSchedule.mutate({
			scheduleId: schedule.id,
			hashedPid: user?.hashedPid,
			...schedule,
		})
	}
	const setAwayValue = (value: number, enabled: boolean) => {
		if (!hvacId || !user?.hashedPid) return

		setAwayMode({
			enabled: enabled,
			savedTemperature: value,
		})

		updateObjectHvac.mutate({
			id: object.id,
			data: {
				hvacAwayMode: enabled,
				savedTemperature: value,
			},
		})
	}

	const getThermostatStatus = useMemo(() => {
		if (!activeHvac) return { icon: <img src='assets/thermo/thermoAuto.svg' />, text: t('auto') }

		if (userSettings?.smartHeatingActive) {
			return {
				icon: (
					<img
						src='assets/thermo/thermoAuto.svg'
						alt={t('heatingControls.altThermoStatStatusIcon')}
						style={{ width: '1.6em', height: '1.6em' }}
					/>
				),
				text: t('heatingControls.smartHeatingActive'),
			}
		}
		if (activeHvac.thermostatState.mode === 'HEAT') {
			return {
				icon: (
					<img
						src='assets/thermo/thermoHeat.png'
						alt={t('heatingControls.altThermoStatStatusIcon')}
						style={{ width: '1.6em', height: '1.6em' }}
					/>
				),
				text: t('modeHeat'),
			}
		}
		if (activeHvac.thermostatState.mode === 'COOL') {
			return {
				icon: (
					<img
						src='assets/thermo/thermoCool.png'
						alt={t('heatingControls.altThermoStatStatusIcon')}
						style={{ width: '1.6em', height: '1.6em' }}
					/>
				),
				text: t('modeCool'),
			}
		}
		if (activeHvac.thermostatState.mode === 'OFF') {
			return {
				icon: (
					<img
						src='assets/thermo/thermoGoal.png'
						alt={t('heatingControls.altThermoStatStatusIcon')}
						style={{ width: '1.6em', height: '1.6em' }}
					/>
				),
				text: t('modeOff'),
			}
		}
		return {
			icon: (
				<img
					src='assets/thermo/thermoHeat.png'
					alt={t('heatingControls.altThermoStatStatusIcon')}
					style={{ width: '1.6em', height: '1.6em' }}
				/>
			),
			text: t('modeHeat'),
		}
	}, [activeHvac])

	return (
		<HvacContext.Provider
			value={{
				hvac: activeHvac,
				onlyDaySchedule: onlyDaySchedule,
				dayAndNightSchedule: dayAndNightSchedule,
				smartPolicy: hvacSmartPolicy,
				setHvacValue,
				setSmartPolicy,
				setSchedule,
				mutationLoading,
				setMutationLoading,
				awayMode,
				setAwayValue,
				thermoStatus: getThermostatStatus,
				error: enodeError,
				unlinkHvac,
				smartStatus,
				hvacs,
				modeSwitchLoading,
				setModeSwitchLoading,
				actionStatus,
			}}
		>
			{children}
		</HvacContext.Provider>
	)
}

export default HvacContextProvider
